<script setup>
/**
 * A LOGO DO ADVUP, VETORIAL E COM MOVIMENTO — "adv up!" montado letra por letra.
 *
 * Substitui o filme (`LogoAnimado.vue`, webm/mov) e a imagem do menu: as letras
 * são os contornos da logo oficial (`advup-logo-ref.png`, vetorizada por letra
 * com potrace em 03/11/2026), então nítidas em qualquer tamanho, no claro e no
 * escuro, sem arquivo de vídeo para baixar.
 *
 * A cena conta o nome: "adv" sobe, "up" sobe atrás, e o "!" CAI como um martelo
 * de audiência — a haste desce, o ponto bate, a marca azul se abre em onda e uma
 * luz passa uma vez pelas letras. Toca uma vez; nada fica em laço.
 *
 * Só `transform`/`opacity`, tempos e curva dos tokens `--mov-*` (então "reduzir
 * movimento" mostra a logo pronta, sem animar). `compacto` é a versão do menu:
 * sem halo; a luz volta a cada 9 s e passar o mouse repete.
 */
import { ref, nextTick } from 'vue';

defineProps({
  // Altura da logo. A largura acompanha pela proporção do desenho.
  altura: { type: String, default: '13rem' },
  // Versão do menu: sem halo de fundo; a luz volta a cada 9 s e o mouse em cima repete.
  compacto: { type: Boolean, default: false },
  // Fundo sempre escuro (o menu): letras brancas nos dois temas.
  sobreEscuro: { type: Boolean, default: false },
});

const idDoRecorte = `la-recorte-${Math.random().toString(36).slice(2, 9)}`;
const idDoHalo = `${idDoRecorte}-halo`;
const idDaLuz = `${idDoRecorte}-luz`;
const cor = 'currentColor';
const NOME = 'advup!';

const tocando = ref(true);
const repetir = async () => {
  if (tocando.value) return;
  tocando.value = false;
  await nextTick();
  // Força o navegador a ler o estado parado antes de religar as animações.
  // eslint-disable-next-line no-unused-expressions
  document.body.offsetWidth;
  tocando.value = true;
};
const terminou = event => {
  if (event.target.classList.contains('la__onda')) tocando.value = false;
};
</script>

<template>
  <span
    class="la"
    :class="{
      'la--toca': tocando,
      'la--compacto': compacto,
      'la--escuro': sobreEscuro,
    }"
    :style="{ '--la-altura': altura }"
    @mouseenter="compacto && repetir()"
  >
    <svg
      class="la__svg"
      viewBox="930 175 1264 1527"
      role="img"
      :aria-label="NOME"
      focusable="false"
    >
      <defs>
        <clipPath :id="idDoRecorte">
          <g transform="translate(942 391)">
            <path
              transform="translate(0 416) scale(1 -1)"
              d="M165 411 c-94 -7 -152 -55 -141 -115 13 -64 111 -61 129 4 1 6 3 30 1 31 0 1 -4 0 -7 -1 -29 -6 -51 16 -40 38 7 14 22 23 46 26 30 4 53 -9 63 -38 7 -18 8 -35 7 -88 l0 -42 -37 0 c-19 0 -42 0 -49 -1 -82 -7 -125 -41 -132 -105 -6 -53 20 -93 69 -109 55 -17 125 1 147 39 3 5 3 5 3 1 1 -7 8 -20 15 -27 9 -9 6 -9 64 -9 l50 0 0 141 c0 88 0 144 -1 150 -7 60 -49 95 -128 103 -18 2 -45 3 -59 2z m58 -265 c1 -70 1 -64 -7 -77 -19 -28 -42 -32 -61 -12 -25 25 -29 79 -10 111 14 24 42 40 71 40 l7 0 0 -62z"
            />
          </g>
          <g transform="translate(1353 186)">
            <path
              transform="translate(0 618) scale(1 -1)"
              d="M306 613 c-14 -2 -22 -6 -28 -13 -8 -9 -7 -2 -8 -115 l0 -101 -12 7 c-95 48 -190 22 -230 -63 -36 -74 -30 -197 11 -260 45 -70 134 -83 222 -34 6 4 11 6 11 6 1 0 1 -3 2 -5 3 -7 9 -13 15 -16 12 -6 14 -6 64 -7 l46 0 0 301 0 301 -44 0 c-25 0 -47 -1 -49 -1z m-56 -242 c4 -1 10 -4 13 -6 l7 -4 -1 -149 c0 -97 -1 -150 -1 -151 -2 -2 -17 -9 -25 -11 -38 -8 -76 29 -92 90 -12 46 -11 104 2 149 17 62 57 95 97 82z"
            />
          </g>
          <g transform="translate(1788 404)">
            <path
              transform="translate(0 392) scale(1 -1)"
              d="M4 387 c1 -1 6 -13 11 -26 6 -13 31 -72 56 -130 59 -139 75 -177 87 -204 l10 -23 29 0 28 1 83 190 c45 105 82 192 82 192 0 1 -5 1 -11 1 l-11 0 -56 -131 c-31 -72 -57 -131 -57 -132 0 -1 -3 4 -5 10 -19 44 -68 162 -83 196 -19 47 -21 49 -31 54 -6 3 -6 3 -69 3 -50 0 -63 0 -63 -1z"
            />
          </g>
          <g transform="translate(1036 1081)">
            <path
              transform="translate(0 407) scale(1 -1)"
              d="M4 402 c0 -1 0 -71 0 -156 1 -171 0 -160 7 -180 8 -25 28 -46 52 -55 29 -11 67 -9 102 4 22 9 47 24 64 39 5 4 9 8 9 8 1 0 1 -4 1 -9 1 -18 9 -27 28 -33 5 -1 12 -1 54 -2 l48 0 0 166 c0 95 0 171 -1 176 -2 19 -7 30 -19 36 -11 6 -16 6 -65 7 l-45 0 0 -156 0 -157 -7 -6 c-37 -32 -78 -28 -92 9 -6 14 -5 7 -6 145 -1 115 -1 128 -2 134 -5 17 -13 25 -31 29 -10 2 -96 3 -97 1z"
            />
          </g>
          <g transform="translate(1467 1073)">
            <path
              transform="translate(0 618) scale(1 -1)"
              d="M227 613 c-29 -4 -59 -14 -84 -29 -11 -6 -11 -6 -13 -2 0 2 -1 3 -1 4 0 2 -7 10 -11 12 -11 7 -12 7 -66 8 l-48 0 0 -301 0 -301 46 0 c51 1 55 1 67 7 7 4 11 8 14 16 3 6 3 6 3 107 l0 101 14 -7 c34 -17 62 -24 98 -23 39 1 70 14 96 39 38 36 56 89 56 164 1 115 -42 186 -120 203 -11 2 -40 4 -51 2z m-39 -46 c10 -4 21 -11 30 -20 59 -59 58 -223 -1 -281 -23 -23 -53 -27 -79 -11 -4 2 -4 2 -4 152 0 150 0 150 3 152 13 9 35 13 51 8z"
            />
          </g>
          <g transform="translate(1923 895)">
            <path
              transform="translate(0 404) scale(1 -1)"
              d="M59 398 c-26 -4 -44 -21 -52 -48 -2 -6 -2 -11 -3 -26 -1 -35 4 -57 31 -155 24 -84 33 -122 34 -149 2 -17 1 -16 11 -16 10 0 9 -2 11 15 3 23 12 61 28 121 30 115 36 146 36 179 0 41 -16 67 -47 78 -11 3 -36 4 -49 1z"
            />
          </g>
        </clipPath>
        <radialGradient :id="idDoHalo" cx="50%" cy="50%" r="50%">
          <stop offset="0" stop-color="var(--la-azul)" stop-opacity="0.2" />
          <stop offset="0.6" stop-color="var(--la-azul)" stop-opacity="0.06" />
          <stop offset="1" stop-color="var(--la-azul)" stop-opacity="0" />
        </radialGradient>
        <linearGradient :id="idDaLuz" x1="0" y1="0" x2="1" y2="0">
          <stop offset="0" stop-color="#fff" stop-opacity="0" />
          <stop offset="0.5" stop-color="#fff" stop-opacity="0.85" />
          <stop offset="1" stop-color="#fff" stop-opacity="0" />
        </linearGradient>
      </defs>

      <circle
        class="la__halo"
        cx="1562"
        cy="940"
        r="760"
        :fill="`url(#${idDoHalo})`"
      />

      <g class="la__letras">
        <g class="la__g la__i0">
          <g transform="translate(942 391)">
            <path
              :fill="cor"
              transform="translate(0 416) scale(1 -1)"
              d="M165 411 c-94 -7 -152 -55 -141 -115 13 -64 111 -61 129 4 1 6 3 30 1 31 0 1 -4 0 -7 -1 -29 -6 -51 16 -40 38 7 14 22 23 46 26 30 4 53 -9 63 -38 7 -18 8 -35 7 -88 l0 -42 -37 0 c-19 0 -42 0 -49 -1 -82 -7 -125 -41 -132 -105 -6 -53 20 -93 69 -109 55 -17 125 1 147 39 3 5 3 5 3 1 1 -7 8 -20 15 -27 9 -9 6 -9 64 -9 l50 0 0 141 c0 88 0 144 -1 150 -7 60 -49 95 -128 103 -18 2 -45 3 -59 2z m58 -265 c1 -70 1 -64 -7 -77 -19 -28 -42 -32 -61 -12 -25 25 -29 79 -10 111 14 24 42 40 71 40 l7 0 0 -62z"
            />
          </g>
        </g>
        <g class="la__g la__i1">
          <g transform="translate(1353 186)">
            <path
              :fill="cor"
              transform="translate(0 618) scale(1 -1)"
              d="M306 613 c-14 -2 -22 -6 -28 -13 -8 -9 -7 -2 -8 -115 l0 -101 -12 7 c-95 48 -190 22 -230 -63 -36 -74 -30 -197 11 -260 45 -70 134 -83 222 -34 6 4 11 6 11 6 1 0 1 -3 2 -5 3 -7 9 -13 15 -16 12 -6 14 -6 64 -7 l46 0 0 301 0 301 -44 0 c-25 0 -47 -1 -49 -1z m-56 -242 c4 -1 10 -4 13 -6 l7 -4 -1 -149 c0 -97 -1 -150 -1 -151 -2 -2 -17 -9 -25 -11 -38 -8 -76 29 -92 90 -12 46 -11 104 2 149 17 62 57 95 97 82z"
            />
          </g>
        </g>
        <g class="la__g la__i2">
          <g transform="translate(1788 404)">
            <path
              :fill="cor"
              transform="translate(0 392) scale(1 -1)"
              d="M4 387 c1 -1 6 -13 11 -26 6 -13 31 -72 56 -130 59 -139 75 -177 87 -204 l10 -23 29 0 28 1 83 190 c45 105 82 192 82 192 0 1 -5 1 -11 1 l-11 0 -56 -131 c-31 -72 -57 -131 -57 -132 0 -1 -3 4 -5 10 -19 44 -68 162 -83 196 -19 47 -21 49 -31 54 -6 3 -6 3 -69 3 -50 0 -63 0 -63 -1z"
            />
          </g>
        </g>
        <g class="la__g la__i4">
          <g transform="translate(1036 1081)">
            <path
              :fill="cor"
              transform="translate(0 407) scale(1 -1)"
              d="M4 402 c0 -1 0 -71 0 -156 1 -171 0 -160 7 -180 8 -25 28 -46 52 -55 29 -11 67 -9 102 4 22 9 47 24 64 39 5 4 9 8 9 8 1 0 1 -4 1 -9 1 -18 9 -27 28 -33 5 -1 12 -1 54 -2 l48 0 0 166 c0 95 0 171 -1 176 -2 19 -7 30 -19 36 -11 6 -16 6 -65 7 l-45 0 0 -156 0 -157 -7 -6 c-37 -32 -78 -28 -92 9 -6 14 -5 7 -6 145 -1 115 -1 128 -2 134 -5 17 -13 25 -31 29 -10 2 -96 3 -97 1z"
            />
          </g>
        </g>
        <g class="la__g la__i5">
          <g transform="translate(1467 1073)">
            <path
              :fill="cor"
              transform="translate(0 618) scale(1 -1)"
              d="M227 613 c-29 -4 -59 -14 -84 -29 -11 -6 -11 -6 -13 -2 0 2 -1 3 -1 4 0 2 -7 10 -11 12 -11 7 -12 7 -66 8 l-48 0 0 -301 0 -301 46 0 c51 1 55 1 67 7 7 4 11 8 14 16 3 6 3 6 3 107 l0 101 14 -7 c34 -17 62 -24 98 -23 39 1 70 14 96 39 38 36 56 89 56 164 1 115 -42 186 -120 203 -11 2 -40 4 -51 2z m-39 -46 c10 -4 21 -11 30 -20 59 -59 58 -223 -1 -281 -23 -23 -53 -27 -79 -11 -4 2 -4 2 -4 152 0 150 0 150 3 152 13 9 35 13 51 8z"
            />
          </g>
        </g>
      </g>

      <!-- O "!": a haste cai, o ponto bate, a onda sai do ponto. -->
      <g class="la__haste">
        <g class="la__g">
          <g transform="translate(1923 895)">
            <path
              class="la__azul"
              transform="translate(0 404) scale(1 -1)"
              d="M59 398 c-26 -4 -44 -21 -52 -48 -2 -6 -2 -11 -3 -26 -1 -35 4 -57 31 -155 24 -84 33 -122 34 -149 2 -17 1 -16 11 -16 10 0 9 -2 11 15 3 23 12 61 28 121 30 115 36 146 36 179 0 41 -16 67 -47 78 -11 3 -36 4 -49 1z"
            />
          </g>
        </g>
      </g>
      <g class="la__ponto-ancora" @animationend="terminou">
        <circle class="la__onda" cx="2002" cy="1414" r="82" />
        <g class="la__g la__ponto">
          <g transform="translate(1917 1328)">
            <path
              class="la__azul"
              transform="translate(0 172) scale(1 -1)"
              d="M69 166 c-64 -14 -87 -93 -40 -138 17 -16 34 -23 57 -23 59 -1 99 62 73 116 -10 21 -27 37 -50 44 -10 3 -30 4 -40 1z"
            />
          </g>
        </g>
      </g>

      <g :clip-path="`url(#${idDoRecorte})`">
        <rect
          class="la__luz"
          x="930"
          y="175"
          width="520"
          height="1527"
          :fill="`url(#${idDaLuz})`"
        />
      </g>
    </svg>
  </span>
</template>

<style scoped>
.la {
  --la-tinta: #232a5c;
  --la-azul: #6bc1de;
  display: inline-block;
  height: var(--la-altura);
  aspect-ratio: 1264 / 1527;
  max-height: 60vh;
  color: var(--la-tinta);
  line-height: 0;
}

.dark .la,
.la--escuro {
  --la-tinta: #f4f6ff;
}

.la__svg {
  display: block;
  width: 100%;
  height: 100%;
  overflow: visible;
}

.la__azul {
  fill: var(--la-azul);
}

.la__i0 {
  --i: 0;
}

.la__i1 {
  --i: 1;
}

.la__i2 {
  --i: 2;
}

.la__i4 {
  --i: 4;
}

.la__i5 {
  --i: 5;
}

.la__g {
  transform-box: fill-box;
  transform-origin: 50% 100%;
}

.la__halo {
  transform-box: fill-box;
  transform-origin: 50% 50%;
}

.la--compacto .la__halo {
  display: none;
}

.la__onda {
  fill: none;
  stroke: var(--la-azul);
  stroke-width: 14;
  opacity: 0;
  transform-box: fill-box;
  transform-origin: 50% 50%;
}

.la__luz {
  opacity: 0;
}

/* Cada letra sobe do chão dela, uma depois da outra (ritmo da casa). A segunda
   linha, "up", entra no meio da primeira e termina depois: o olho sobe junto. */
.la--toca .la__letras .la__g {
  animation: laSobe var(--mov-5) var(--mov-curva) backwards;
  animation-delay: calc(var(--cascata-passo) * var(--i, 0) * 2);
}

.la--toca .la__haste .la__g {
  transform-origin: 50% 0;
  animation: laHaste var(--mov-5) var(--mov-curva) backwards;
  animation-delay: calc(var(--cascata-passo) * 14);
}

.la--toca .la__ponto {
  animation: laPonto var(--mov-5) var(--mov-curva) backwards;
  animation-delay: calc(var(--cascata-passo) * 18);
}

.la--toca .la__onda {
  animation: laOnda calc(var(--mov-5) + var(--mov-4)) var(--mov-curva) backwards;
  animation-delay: calc(var(--cascata-passo) * 18 + var(--mov-4));
}

.la--toca .la__halo {
  animation: laHalo var(--mov-6) var(--mov-curva) backwards;
}

.la--toca .la__luz {
  animation: laLuz var(--mov-6) var(--mov-curva) backwards;
  animation-delay: calc(var(--cascata-passo) * 28);
}

@keyframes laSobe {
  from {
    opacity: 0;
    transform: translateY(70px) scale(0.94);
    filter: blur(3px);
  }
}

@keyframes laHaste {
  from {
    opacity: 0;
    transform: translateY(-360px) scaleY(1.12);
  }
  70% {
    opacity: 1;
  }
}

/* O ponto cai com impulso, achata no choque e se assenta — o único gesto com
   peso da cena. */
@keyframes laPonto {
  from {
    opacity: 0;
    transform: translateY(-420px) scaleY(1.2);
  }
  58% {
    opacity: 1;
    transform: translateY(0) scale(1.12, 0.8);
  }
  to {
    transform: translateY(0) scale(1);
  }
}

@keyframes laOnda {
  from {
    opacity: 0.8;
    transform: scale(0.8);
  }
  to {
    opacity: 0;
    transform: scale(5.2);
  }
}

@keyframes laHalo {
  from {
    opacity: 0;
    transform: scale(0.55);
  }
}

@keyframes laLuz {
  from {
    opacity: 0;
    transform: translateX(0);
  }
  15% {
    opacity: 0.55;
  }
  to {
    opacity: 0;
    transform: translateX(1250px);
  }
}

/* Menu: a luz volta de tempos em tempos, como no UP!Chat (a cada 9 s). */
@media (prefers-reduced-motion: no-preference) {
  .la--compacto:not(.la--toca) .la__luz {
    animation: laLuz var(--mov-6) var(--mov-curva) 9s infinite;
  }
}

@media (prefers-reduced-motion: reduce) {
  .la__onda,
  .la__luz {
    display: none;
  }
}
</style>
